import { loadWorkspaceContentFile, loadWorkspaceContentTree } from "./loaders.js";
import {
  downloadSnapshot,
  refreshImportRecord,
  submitDeletePath,
  submitDropUpload,
  submitFileEdit,
  submitFileUpload,
  submitSnapshotImport,
  submitTreeUpload
} from "./mutations.js";

export function handleWorkspaceContentInput(state, target) {
  if (!(target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement)) {
    return false;
  }
  if (!target.closest("[data-workspace-content-panel]")) {
    return false;
  }
  if (target instanceof HTMLInputElement && target.type === "file") {
    const nextFile = target.files && target.files.length ? target.files[0] : null;
    const name = String(target.name || "");
    if (name === "content_file_upload_file") {
      state.fileUploadFile = nextFile;
      state.fileUploadName = nextFile ? nextFile.name : "";
    } else if (name === "content_tree_upload_archive") {
      state.treeUploadFile = nextFile;
      state.treeUploadName = nextFile ? nextFile.name : "";
    } else {
      state.importFile = nextFile;
      state.importFileName = nextFile ? nextFile.name : "";
    }
    return true;
  }
  const name = String(target.name || "");
  if (name === "content_ref") { state.refInput = target.value; return true; }
  if (name === "content_file_upload_path") { state.fileUploadPath = target.value; return true; }
  if (name === "content_file_upload_executable" && target instanceof HTMLInputElement) { state.fileUploadExecutable = Boolean(target.checked); return true; }
  if (name === "content_tree_upload_path") { state.treeUploadPath = target.value; return true; }
  if (name === "content_delete_path") { state.deletePath = target.value; return true; }
  if (name === "import_message") { state.importMessage = target.value; return true; }
  if (name === "import_expected_base_commit") { state.importExpectedBaseCommit = target.value; return true; }
  if (name === "file_edit_content") { state.fileDraftContent = target.value; return true; }
  return false;
}

export async function handleWorkspaceContentClick(state, target, options = {}) {
  if (!(target instanceof Element)) return false;
  const apiKey = options.apiKey || "";
  const workspaceItem = options.workspaceItem || null;
  const workspaceID = state.workspaceID;
  const renderFn = options.render;
  const actionOptions = {
    render: renderFn,
    setStatus: options.setStatus,
    refreshCollections: options.refreshCollections,
    request: options.request
  };

  const withinPanel = Boolean(target.closest("[data-workspace-content-panel]"));
  if (!withinPanel) {
    return false;
  }

  const setActiveMutation = target.closest("[data-action='set-active-mutation']");
  if (setActiveMutation) {
    const mutation = setActiveMutation.getAttribute("data-mutation") || "";
    state.activeMutation = state.activeMutation === mutation ? "" : mutation;
    renderFn?.();
    return true;
  }

  const toggleRefInput = target.closest("[data-action='toggle-ref-input']");
  if (toggleRefInput) {
    state.refInputVisible = !state.refInputVisible;
    renderFn?.();
    return true;
  }

  const backToTree = target.closest("[data-action='back-to-tree']");
  if (backToTree) {
    Object.assign(state, { selectedFilePath: "", file: null, fileError: "", fileEditing: false, fileSaving: false, fileDraftContent: null });
    renderFn?.();
    return true;
  }

  const toggleActions = target.closest("[data-action='toggle-wc-actions']");
  if (toggleActions) {
    state.actionsVisible = !state.actionsVisible;
    renderFn?.();
    return true;
  }

  const dismissFeedback = target.closest("[data-action='dismiss-feedback']");
  if (dismissFeedback) {
    state.feedbackDismissed = true;
    state.mutationError = "";
    renderFn?.();
    return true;
  }

  const confirmDropUpload = target.closest("[data-action='confirm-drop-upload']");
  if (confirmDropUpload) {
    const files = state.dropPendingFiles;
    state.dropPendingFiles = null;
    if (files && files.length > 0) {
      submitDropUpload(state, apiKey, workspaceID, workspaceItem, files, actionOptions).catch(() => {});
    }
    return true;
  }

  const cancelDropUpload = target.closest("[data-action='cancel-drop-upload']");
  if (cancelDropUpload) {
    state.dropPendingFiles = null;
    renderFn?.();
    return true;
  }

  const removeDropFile = target.closest("[data-action='remove-drop-file']");
  if (removeDropFile) {
    const idx = Number(removeDropFile.getAttribute("data-drop-index"));
    if (state.dropPendingFiles) {
      const arr = Array.from(state.dropPendingFiles);
      arr.splice(idx, 1);
      state.dropPendingFiles = arr.length > 0 ? arr : null;
    }
    renderFn?.();
    return true;
  }

  const startFileEdit = target.closest("[data-action='start-file-edit']");
  if (startFileEdit) {
    const file = state.file;
    Object.assign(state, { fileEditing: true, fileSaving: false, fileDraftContent: file ? file.content : "", mutationError: "" });
    renderFn?.();
    return true;
  }

  const cancelFileEdit = target.closest("[data-action='cancel-file-edit']");
  if (cancelFileEdit) {
    Object.assign(state, { fileEditing: false, fileSaving: false, fileDraftContent: null });
    renderFn?.();
    return true;
  }

  const saveFileEdit = target.closest("[data-action='save-file-edit']");
  if (saveFileEdit) {
    await submitFileEdit(state, apiKey, workspaceID, workspaceItem, actionOptions);
    return true;
  }

  const reloadWorkspaceContent = target.closest("[data-action='reload-workspace-content']");
  if (reloadWorkspaceContent) {
    await loadWorkspaceContentTree(state, apiKey, workspaceID, {
      ref: state.refInput,
      path: state.path || "",
      preserveFile: false,
      render: renderFn,
      request: options.request
    });
    state.refInputVisible = false;
    options.setStatus?.("Canonical content refreshed.", "ok");
    renderFn?.();
    return true;
  }

  const openWorkspaceRoot = target.closest("[data-action='open-workspace-root']");
  if (openWorkspaceRoot) {
    await loadWorkspaceContentTree(state, apiKey, workspaceID, {
      ref: state.refInput,
      path: "",
      preserveFile: false,
      render: renderFn,
      request: options.request
    });
    return true;
  }

  const openWorkspacePath = target.closest("[data-action='open-workspace-path']");
  if (openWorkspacePath) {
    const nextPath = openWorkspacePath.getAttribute("data-workspace-path") || "";
    await loadWorkspaceContentTree(state, apiKey, workspaceID, {
      ref: state.refInput,
      path: nextPath,
      preserveFile: false,
      render: renderFn,
      request: options.request
    });
    return true;
  }

  const openWorkspaceEntry = target.closest("[data-action='open-workspace-entry']");
  if (openWorkspaceEntry) {
    const entryPath = openWorkspaceEntry.getAttribute("data-workspace-path") || "";
    const entryKind = openWorkspaceEntry.getAttribute("data-workspace-kind") || "";
    if (entryKind === "dir") {
      await loadWorkspaceContentTree(state, apiKey, workspaceID, {
        ref: state.refInput,
        path: entryPath,
        preserveFile: false,
        render: renderFn,
        request: options.request
      });
    } else {
      await loadWorkspaceContentFile(state, apiKey, workspaceID, entryPath, {
        render: renderFn,
        request: options.request
      });
    }
    return true;
  }

  const submitFileUploadAction = target.closest("[data-action='submit-workspace-file-upload']");
  if (submitFileUploadAction) {
    await submitFileUpload(state, apiKey, workspaceID, workspaceItem, actionOptions);
    return true;
  }

  const submitTreeUploadAction = target.closest("[data-action='submit-workspace-tree-upload']");
  if (submitTreeUploadAction) {
    await submitTreeUpload(state, apiKey, workspaceID, workspaceItem, actionOptions);
    return true;
  }

  const submitDeleteAction = target.closest("[data-action='submit-workspace-delete-path']");
  if (submitDeleteAction) {
    await submitDeletePath(state, apiKey, workspaceID, workspaceItem, actionOptions);
    return true;
  }

  const exportAction = target.closest("[data-action='export-workspace-content']");
  if (exportAction) {
    await downloadSnapshot(state, apiKey, workspaceID, actionOptions);
    return true;
  }

  const submitImportAction = target.closest("[data-action='submit-workspace-import']");
  if (submitImportAction) {
    await submitSnapshotImport(state, apiKey, workspaceID, workspaceItem, actionOptions);
    return true;
  }

  const refreshImportAction = target.closest("[data-action='refresh-workspace-import']");
  if (refreshImportAction) {
    const importID = refreshImportAction.getAttribute("data-import-id") || "";
    await refreshImportRecord(state, apiKey, workspaceID, importID, { clearError: true, render: renderFn });
    options.setStatus?.("Import status refreshed.", "ok");
    return true;
  }

  return false;
}

export function handleWorkspaceContentDragDrop(state, event, options = {}) {
  const target = event.target;
  if (!(target instanceof Element)) return false;
  const dropTarget = target.closest(".wc-drop-target");
  if (!dropTarget) return false;

  const type = event.type;

  if (type === "dragover") {
    event.preventDefault();
    event.dataTransfer.dropEffect = "copy";
    dropTarget.classList.add("wc-drop-target--active");
    return true;
  }

  if (type === "dragleave") {
    if (!dropTarget.contains(event.relatedTarget)) {
      dropTarget.classList.remove("wc-drop-target--active");
    }
    return true;
  }

  if (type === "drop") {
    event.preventDefault();
    dropTarget.classList.remove("wc-drop-target--active");
    const files = event.dataTransfer?.files;
    if (!files || files.length === 0) return true;
    state.dropPendingFiles = files;
    state.feedbackDismissed = false;
    options.render?.();
    return true;
  }

  return false;
}
